import { Combobox, NoData } from '@/components';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React from "react";
import { Bar } from "react-chartjs-2";
import useCollectionsSummary from "./useCollectionsSummary";
import { UI_TEXT } from "./constants";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CollectionsSummary: React.FC = () => {
  const {
    collectionsSummary,
    chartData,
    chartOptions,
    selectedYear,
    setSelectedYear,
    selectedSchool,
    setSelectedSchool,
    schoolOptions,
    years,
    isLoading,
    error,
  } = useCollectionsSummary();

  if (isLoading) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            {UI_TEXT.TITLE}
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">{UI_TEXT.LOADING}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            {UI_TEXT.TITLE}
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">
              {UI_TEXT.ERROR_PREFIX} {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto">
      <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-5">
          <h2 className="text-lg font-medium text-gray-800 mb-4 lg:mb-0">
            {UI_TEXT.TITLE}
          </h2>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="w-full sm:w-32">
              <Combobox
                options={years}
                value={selectedYear.toString()}
                onSelect={(value) => setSelectedYear(parseInt(value))}
                placeholder={UI_TEXT.PLACEHOLDERS.YEAR}
                searchPlaceholder={UI_TEXT.PLACEHOLDERS.SEARCH_YEARS}
                noDataPlaceholder={UI_TEXT.PLACEHOLDERS.NO_YEARS}
              />
            </div>
            <div className="w-full sm:w-48">
              <Combobox
                options={schoolOptions}
                value={selectedSchool}
                onSelect={setSelectedSchool}
                placeholder={UI_TEXT.PLACEHOLDERS.SCHOOL}
                searchPlaceholder={UI_TEXT.PLACEHOLDERS.SEARCH_SCHOOLS}
                noDataPlaceholder={UI_TEXT.PLACEHOLDERS.NO_SCHOOLS}
              />
            </div>
          </div>
        </div>

        {/* Chart Container */}
        <div className="h-100">
          {collectionsSummary.length === 0 ? (
            <NoData
              title={UI_TEXT.NO_DATA_TITLE}
              description={UI_TEXT.NO_DATA_DESCRIPTION}
            />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionsSummary;
